import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useIntl, defineMessage } from 'react-intl';
import { graphql, useFragment, useMutation } from 'react-relay';

import type { DocNode } from '@atlaskit/adf-schema';
import Button, { IconButton, SplitButton } from '@atlaskit/button/new';
import { cssMap, cx } from '@atlaskit/css';
import DropdownMenu, { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu';
import { ProviderFactory } from '@atlaskit/editor-common/provider-factory';
import { useFlags } from '@atlaskit/flag';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import StatusErrorIcon from '@atlaskit/icon/core/status-error';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Pressable, Text } from '@atlaskit/primitives/compiled';
import { ReactRenderer } from '@atlaskit/renderer';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { adfToMarkdownPlus } from '@atlassian/adf-markdown-transformer';
import { encodeAdf } from '@atlassian/conversation-assistant-content-transformer/adf-encoder';
import { hydrateLinkingPlatformCache } from '@atlassian/conversation-assistant-content-transformer/linking-platform';
import { fireErrorAnalytics } from '@atlassian/error-handling';

import type { AgenticPlanTool$key } from './__generated__/AgenticPlanTool.graphql';
import type { AgenticPlanToolMutation } from './__generated__/AgenticPlanToolMutation.graphql';
import { AgenticPlanEditor, type AgenticPlanEditorActions } from './AgenticPlanEditor.tsx';
import { PACKAGE_NAME, TEAM_NAME } from './constants.tsx';
import { defaultFooter } from './DefaultWrappers.tsx';
import { getPlanEditLevel } from './planEditAnalytics';
import type { AgenticPlanToolProps, PlanEditLevel } from './types.tsx';
import { updateDescriptionUfoExperience } from './updateDescriptionUfoExperience.tsx';

const moreOptionsButtonLabel = defineMessage({
	id: 'agentic-planning.AgenticPlanTool.moreOptionsButtonLabel',
	defaultMessage: 'More Options',
	description: 'Button label to show more options for the proposed agentic plan',
});
const startWorkingButtonLabel = defineMessage({
	id: 'agentic-planning.AgenticPlanTool.startWorkingButtonLabel',
	defaultMessage: 'Start working',
	description: 'Button label to start working on the proposed agentic plan',
});
const startWorkingTooltipMessage = defineMessage({
	id: 'agentic-planning.AgenticPlanTool.startWorkingTooltip',
	defaultMessage: 'Add plan to Description and start working',
	description: 'Tooltip for the button to start working on the proposed agentic plan',
});
const updateDescriptionButtonLabel = defineMessage({
	id: 'agentic-planning.AgenticPlanTool.updateDescriptionButtonLabel',
	defaultMessage: 'Update description',
	description:
		'Button label to add the proposed agentic plan to the issue description and complete the session',
});
const continueWithoutPublishButtonLabel = defineMessage({
	id: 'agentic-planning.AgenticPlanTool.continueWithoutPublishButtonLabel',
	defaultMessage: 'Continue without publishing',
	description: 'Button label to continue the proposed plan action without publishing',
});
const continueButtonLabel = defineMessage({
	id: 'agentic-planning.AgenticPlanTool.continueButtonLabel',
	defaultMessage: 'Continue',
	description: 'Button label to continue the proposed plan action',
});
const showMoreButtonLabel = defineMessage({
	id: 'agentic-planning.AgenticPlanTool.showMoreButtonLabel',
	defaultMessage: 'Show more',
	description: 'Button label to expand the collapsed plan content',
});
const showLessButtonLabel = defineMessage({
	id: 'agentic-planning.AgenticPlanTool.showLessButtonLabel',
	defaultMessage: 'Show less',
	description: 'Button label to collapse the expanded plan content',
});
const editPlanLabel = defineMessage({
	id: 'agentic-planning.AgenticPlanTool.editPlanLabel',
	defaultMessage: 'Edit plan',
	description: 'Accessible label for the proposed agentic plan edit area',
});

const titleErrorFlagMessages = defineMessage({
	id: 'agentic-planning.AgenticPlanTool.titleErrorFlagMessages',
	defaultMessage: 'Something went wrong',
	description: 'Title for the error flag shown when updating the issue description fails',
});

const contentErrorFlagMessages = defineMessage({
	id: 'agentic-planning.AgenticPlanTool.contentErrorFlagMessages',
	defaultMessage: "We couldn't update the issue description. Please try again.",
	description: 'Content for the error flag shown when updating the issue description fails',
});

const tryAgainErrorFlagMessages = defineMessage({
	id: 'agentic-planning.AgenticPlanTool.tryAgainErrorFlagMessages',
	defaultMessage: 'Try again',
	description: 'Action label for the error flag shown when updating the issue description fails',
});

const MAX_COLLAPSED_HEIGHT = 135;

const styles = cssMap({
	editorWrapper: {
		minHeight: '32px',
		position: 'relative',
	},
	editableEditorWrapper: {
		cursor: 'pointer',
		marginTop: token('space.negative.150'),
		marginRight: token('space.negative.150'),
		marginLeft: token('space.negative.150'),
		paddingTop: token('space.150'),
		paddingRight: token('space.150'),
		paddingLeft: token('space.150'),
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
	},
	readOnlyEditorWrapper: {
		cursor: 'default',
	},
	contentContainer: {
		position: 'relative',
	},
	contentCollapsed: {
		maxHeight: `${MAX_COLLAPSED_HEIGHT}px`,
		overflow: 'hidden',
	},
	contentExpanded: {
		overflow: 'visible',
	},
	contentExpandedChat: {
		paddingBottom: token('space.500'),
	},
	contentCollapsedMask: {
		maskImage:
			'linear-gradient(to bottom, rgba(0,0,0,1) 0%, rgba(0,0,0,1) 65%, rgba(0,0,0,0) 100%)',
	},
	showMoreButtonWrapper: {
		position: 'absolute',
		bottom: token('space.100'),
		left: '0',
		right: '0',
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		zIndex: 1,
	},
	showLessButtonWrapper: {
		position: 'absolute',
		bottom: '0',
		left: '0',
		right: '0',
		height: '48px',
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		zIndex: 1,
		opacity: 0,
		transitionProperty: 'opacity',
		transitionDuration: token('motion.duration.medium', '200ms'),
		transitionTimingFunction: token('motion.easing.inout.bold', 'ease-in-out'),
		'&:hover': {
			opacity: 1,
		},
	},
	expandToggleButton: {
		borderRadius: token('radius.full'),
		boxShadow: token('elevation.shadow.overlay'),
		backgroundColor: token('elevation.surface'),
		paddingBlock: token('space.050'),
		paddingInline: token('space.150'),
		color: token('color.text.subtle'),
		'&:hover': {
			backgroundColor: token('elevation.surface.hovered'),
			color: token('color.text'),
		},
		'&:active': {
			backgroundColor: token('elevation.surface.pressed'),
		},
	},
	readViewClickTarget: {
		position: 'absolute',
		top: '0',
		right: '0',
		bottom: '0',
		left: '0',
		cursor: 'text',
		backgroundColor: 'transparent',
	},
});

export type AgenticPlanToolData = {
	content: string;
	issueKey: string;
};

function isAgenticPlanToolData(data: unknown): data is AgenticPlanToolData {
	if (typeof data !== 'object' || data === null) return false;
	const content = Reflect.get(data, 'content');
	const issueKey = Reflect.get(data, 'issueKey');
	return typeof content === 'string' && typeof issueKey === 'string';
}

export function AgenticPlanTool({
	message,
	onExecuteAgent,
	footer = defaultFooter,
	ContentWrapperComponent = React.Fragment,
	isFromChat = false,
	hideFooter = false,
	issueRef = null,
	isLoadingIssue = false,
	onScrollIntoView,
}: AgenticPlanToolProps): React.JSX.Element {
	const { formatMessage } = useIntl();
	const { showFlag } = useFlags();
	const [isUpdatingDescription, setIsUpdatingDescription] = useState(false);

	const action = message.actions?.[0];
	const actionData = isAgenticPlanToolData(action?.data) ? action.data : undefined;
	const content = actionData?.content ?? '';

	const [isExpanded, setIsExpanded] = useState(false);
	const [isOverflowing, setIsOverflowing] = useState(false);
	const [isLinkingPlatformReady, setIsLinkingPlatformReady] = useState(false);
	const [isEditing, setIsEditing] = useState(false);
	const [editedAdf, setEditedAdf] = useState<DocNode | null>(null);

	const descriptionData = useFragment<AgenticPlanTool$key>(
		graphql`
			fragment AgenticPlanTool on JiraIssue {
				key
				descriptionField {
					id
					richText {
						adfValue {
							json
						}
					}
				}
			}
		`,
		issueRef,
	);

	const issueKey = descriptionData?.key ?? actionData?.issueKey ?? '';
	const showOnlyContinueButton = !isLoadingIssue && descriptionData?.descriptionField?.id == null;

	const editorActionsRef = useRef<AgenticPlanEditorActions | null>(null);
	const footerRef = useRef<HTMLDivElement | null>(null);

	const handleContentRef = useCallback((node: HTMLDivElement | null) => {
		if (!node) return;
		setIsOverflowing(node.scrollHeight > MAX_COLLAPSED_HEIGHT);
	}, []);

	// Hydrate the Linking Platform provider regex cache so smart links always render
	useEffect(() => {
		// eslint-disable-next-line @atlaskit/platform/no-direct-web-storage-usage -- existing usage
		hydrateLinkingPlatformCache(globalThis.localStorage).finally(() => {
			setIsLinkingPlatformReady(true);
		});
	}, []);

	// Parse content string → ADF DocNode once, memoised on content change and hydration of smart links cache
	const parsedAdf = useMemo<DocNode>(() => encodeAdf(content), [content, isLinkingPlatformReady]); // eslint-disable-line react-hooks/exhaustive-deps

	const [commitMutation] = useMutation<AgenticPlanToolMutation>(graphql`
		mutation AgenticPlanToolMutation($input: JiraUpdateRichTextFieldInput!) {
			jira {
				updateRichTextField(input: $input) {
					success
					errors {
						message
					}
					field {
						richText {
							adfValue {
								json
							}
						}
					}
				}
			}
		}
	`);

	const showUpdateDescriptionErrorFlag = useCallback(
		({
			errorMsg,
			isRetry,
			onTryAgain,
		}: {
			errorMsg: string;
			isRetry?: boolean;
			onTryAgain?: () => void;
		}) => {
			fireErrorAnalytics({
				meta: {
					id: 'agenticPlanToolUpdateDescription',
					packageName: PACKAGE_NAME,
					teamName: TEAM_NAME,
				},
				attributes: { error: errorMsg, issueKey },
			});
			showFlag({
				id: 'agentic-plan-tool-update-description-error',
				icon: <StatusErrorIcon color={token('color.icon.danger')} label="" />,
				title: formatMessage(titleErrorFlagMessages),
				description: formatMessage(contentErrorFlagMessages),
				actions: onTryAgain
					? [
							{
								content: formatMessage(tryAgainErrorFlagMessages),
								onClick: onTryAgain,
							},
						]
					: undefined,
			});
			if (!isRetry) {
				updateDescriptionUfoExperience.failure();
			}
			setIsUpdatingDescription(false);
		},
		[formatMessage, issueKey, showFlag],
	);

	const resolveCurrentAdf = useCallback(async (): Promise<DocNode> => {
		if (isEditing && editorActionsRef.current) {
			const editorValue = await editorActionsRef.current.getValue();
			if (editorValue) {
				return editorValue as DocNode;
			}
		}
		return editedAdf ?? parsedAdf;
	}, [editedAdf, isEditing, parsedAdf]);

	const getPlanEditLevelForAdf = useCallback(
		(latestAdf: DocNode): PlanEditLevel => {
			const originalPlanMarkdown = adfToMarkdownPlus(parsedAdf).markdown;
			const submittedPlanMarkdown = adfToMarkdownPlus(latestAdf).markdown;

			return getPlanEditLevel(originalPlanMarkdown, submittedPlanMarkdown);
		},
		[parsedAdf],
	);

	const applyEditorChanges = useCallback(
		(latestAdf: DocNode) => {
			if (!isEditing) {
				return;
			}

			setEditedAdf(latestAdf);
			setIsExpanded(true);
			setIsEditing(false);
		},
		[isEditing],
	);

	const handleConfirm = useCallback(
		async ({ isRetry }: { isRetry?: boolean } = {}) => {
			// When not editing, resolve synchronously to avoid microtask deferral
			// (important so that retry callbacks fire commitMutation in the same tick).
			const latestAdf = isEditing ? await resolveCurrentAdf() : (editedAdf ?? parsedAdf);
			setIsUpdatingDescription(true);
			applyEditorChanges(latestAdf);
			if (!isRetry) {
				updateDescriptionUfoExperience.start();
			}

			const descriptionFieldId = descriptionData?.descriptionField?.id ?? undefined;
			const issueDescription: DocNode | undefined =
				descriptionData?.descriptionField?.richText?.adfValue?.json ?? undefined;

			if (!descriptionFieldId) {
				showUpdateDescriptionErrorFlag({
					errorMsg: 'Description field not found for issue',
					isRetry,
					onTryAgain: () => handleConfirm({ isRetry: true }),
				});
				return;
			}

			const existingContent: DocNode['content'] = issueDescription?.content ?? [];
			const planContent: DocNode['content'] = latestAdf.content ?? [];
			const updatedDescription: DocNode = {
				type: 'doc',
				version: 1,
				content: [
					...existingContent,
					...(existingContent.length > 0 && planContent.length > 0
						? [{ type: 'rule' as const }]
						: []),
					...planContent,
				],
			};

			commitMutation({
				variables: {
					input: {
						id: descriptionFieldId,
						operation: {
							document: { jsonValue: updatedDescription, version: updatedDescription.version },
							operation: 'SET' as const,
						},
					},
				},
				onCompleted(data) {
					const payload = data?.jira?.updateRichTextField;
					if (!payload?.success) {
						const errorMessage =
							payload?.errors?.map((e: { message?: string | null }) => e.message).join(', ') ??
							'Unknown error';
						showUpdateDescriptionErrorFlag({
							errorMsg: errorMessage,
							isRetry,
							onTryAgain: () => handleConfirm({ isRetry: true }),
						});
					} else {
						if (!isRetry) {
							updateDescriptionUfoExperience.success();
						}
						onScrollIntoView?.();
						setIsUpdatingDescription(false);
					}
				},
				onError(error) {
					showUpdateDescriptionErrorFlag({
						errorMsg: error.message,
						isRetry,
						onTryAgain: () => handleConfirm({ isRetry: true }),
					});
				},
			});

			if (!isRetry) {
				onExecuteAgent(latestAdf, issueKey, true, getPlanEditLevelForAdf(latestAdf));
			}
		},
		[
			applyEditorChanges,
			commitMutation,
			descriptionData,
			editedAdf,
			getPlanEditLevelForAdf,
			isEditing,
			issueKey,
			onExecuteAgent,
			parsedAdf,
			resolveCurrentAdf,
			showUpdateDescriptionErrorFlag,
			onScrollIntoView,
		],
	);

	const startEditing = useCallback(() => {
		setIsEditing(true);
		setIsExpanded(true);
	}, []);

	const currentAdf = editedAdf ?? parsedAdf;
	const isPlanEditable = !isFromChat;

	const handleEditorReady = useCallback((editorActions: AgenticPlanEditorActions) => {
		editorActionsRef.current = editorActions;
	}, []);

	const handleEditorBlur = useCallback(async () => {
		try {
			const latestAdf = await resolveCurrentAdf();
			applyEditorChanges(latestAdf);
		} catch (error) {
			fireErrorAnalytics({
				meta: {
					id: 'agenticPlanToolEditorBlur',
					packageName: PACKAGE_NAME,
					teamName: TEAM_NAME,
				},
				attributes: {
					error: 'Unknown error during editor blur',
					errorType: error instanceof Error ? error.name : typeof error,
					issueKey,
				},
			});
		}
	}, [applyEditorChanges, issueKey, resolveCurrentAdf]);

	const handleExpandToggle = useCallback(async () => {
		if (!isExpanded) {
			setIsExpanded(true);
			if (isPlanEditable) {
				setIsEditing(true);
			}
			return;
		}

		if (isEditing) {
			const latestAdf = await resolveCurrentAdf();
			setEditedAdf(latestAdf);
			setIsEditing(false);
		}
		setIsExpanded(false);
	}, [isEditing, isExpanded, isPlanEditable, resolveCurrentAdf]);

	const handleContentBlur = useCallback(
		(event: React.FocusEvent<HTMLDivElement>) => {
			// Only commit on blur while the editor is active.
			if (!isEditing) {
				return;
			}

			// Keep editing when focus stays inside the editor content region (for example
			// editor → editor toolbar) or moves to the footer action buttons. Footer actions
			// commit explicitly via their own handlers, so blur must NOT fire on editor → footer
			// (otherwise the resulting re-render swallows the button click → double-click bug).
			if (
				event.relatedTarget instanceof Node &&
				(event.currentTarget.contains(event.relatedTarget) ||
					footerRef.current?.contains(event.relatedTarget) === true)
			) {
				return;
			}

			handleEditorBlur();
		},
		[handleEditorBlur, isEditing],
	);

	const handleExecute = useCallback(async () => {
		const latestAdf = await resolveCurrentAdf();
		// Commit the editor state on the click itself so footer actions take effect in a single
		// click without depending on the editor blur firing first.
		applyEditorChanges(latestAdf);
		onExecuteAgent(latestAdf, issueKey, false, getPlanEditLevelForAdf(latestAdf));
	}, [applyEditorChanges, getPlanEditLevelForAdf, onExecuteAgent, resolveCurrentAdf, issueKey]);

	const actionButtons = useMemo(() => {
		if (fg('jira_ai_task_level_planning_ootb_agent')) {
			return (
				<Button
					appearance="primary"
					onClick={() => handleConfirm()}
					isLoading={isUpdatingDescription}
				>
					{formatMessage(updateDescriptionButtonLabel)}
				</Button>
			);
		}

		if (showOnlyContinueButton) {
			return (
				<Button appearance="primary" onClick={handleExecute}>
					{formatMessage(continueButtonLabel)}
				</Button>
			);
		}

		return (
			<SplitButton appearance="primary">
				<Tooltip content={formatMessage(startWorkingTooltipMessage)}>
					{(tooltipProps) => (
						<Button
							{...tooltipProps}
							onClick={() => handleConfirm()}
							isLoading={isUpdatingDescription}
						>
							{formatMessage(startWorkingButtonLabel)}
						</Button>
					)}
				</Tooltip>
				<DropdownMenu<HTMLButtonElement>
					shouldRenderToParent
					placement="bottom-end"
					trigger={({ triggerRef, ...triggerProps }) => (
						<IconButton
							ref={triggerRef}
							{...triggerProps}
							icon={ChevronDownIcon}
							label={formatMessage(moreOptionsButtonLabel)}
						/>
					)}
				>
					<DropdownItemGroup>
						<DropdownItem onClick={handleExecute}>
							{formatMessage(continueWithoutPublishButtonLabel)}
						</DropdownItem>
					</DropdownItemGroup>
				</DropdownMenu>
			</SplitButton>
		);
	}, [formatMessage, handleConfirm, handleExecute, isUpdatingDescription, showOnlyContinueButton]);

	const dataProviders = useMemo(() => new ProviderFactory(), []);
	useEffect(() => () => dataProviders.destroy(), [dataProviders]);

	const readOnlyContent = (
		<Box
			ref={handleContentRef}
			xcss={cx(
				styles.editorWrapper,
				isPlanEditable ? styles.editableEditorWrapper : styles.readOnlyEditorWrapper,
				isExpanded ? styles.contentExpanded : styles.contentCollapsed,
				!isExpanded && isOverflowing ? styles.contentCollapsedMask : undefined,
				isExpanded && isFromChat ? styles.contentExpandedChat : undefined,
			)}
		>
			<ReactRenderer appearance="comment" document={currentAdf} dataProviders={dataProviders} />
			{isPlanEditable && (
				<Pressable
					xcss={styles.readViewClickTarget}
					aria-label={formatMessage(editPlanLabel)}
					onClick={startEditing}
				/>
			)}
		</Box>
	);

	const expandToggle = isOverflowing && (
		<Box xcss={isExpanded ? styles.showLessButtonWrapper : styles.showMoreButtonWrapper}>
			<Pressable
				xcss={styles.expandToggleButton}
				onMouseDown={(event) => event.preventDefault()}
				onClick={handleExpandToggle}
			>
				<Text size="small" weight="medium">
					{formatMessage(isExpanded ? showLessButtonLabel : showMoreButtonLabel)}
				</Text>
			</Pressable>
		</Box>
	);

	return (
		<Box>
			<Box xcss={styles.contentContainer} onBlur={handleContentBlur}>
				<ContentWrapperComponent>
					{isEditing ? (
						<>
							<AgenticPlanEditor defaultValue={currentAdf} onEditorReady={handleEditorReady} />
							{expandToggle}
						</>
					) : (
						<>
							{readOnlyContent}
							{expandToggle}
						</>
					)}
				</ContentWrapperComponent>
			</Box>
			{!hideFooter && <div ref={footerRef}>{footer(actionButtons)}</div>}
		</Box>
	);
}
